import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Descriptions,
  Popconfirm,
  Alert,
  Spin,
  Tooltip
} from "antd";
import {
  SendOutlined,
  StopOutlined,
  UnlockOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { apiRequest } from "@vojomo-core/shared";
import { useNavigate, useParams } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";

const { Title, Text } = Typography;

interface WorkflowDetail {
  _id: string;
  name: string;
  status: "draft" | "active" | "completed";
  company: string;
  module_type?: string;
  assignment_url?: string;
  subject: string;
  body: string;
  channels: string[];
  deadline?: string;
  no_deadline: boolean;
  lockdown_enabled: boolean;
  total_participants: number;
  completed_count: number;
  created_at: string;
}

interface Participant {
  _id: string;
  user_id: string;
  name: string;
  email: string;
  status: "pending" | "completed" | "overdue";
  sent_at?: string;
  completed_at?: string;
  access_override: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  draft: "default",
  active: "blue",
  completed: "green",
  pending: "orange",
  overdue: "red"
};

const MODULE_LABELS: Record<string, string> = {
  qbr_scoring: "QBR Scoring",
  roadmap_builder: "Roadmap Builder",
  topic_doc: "Topic Document"
};

export function CoordinatorDetail(): React.ReactElement {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  const load = useCallback(async () => {
    if (!workflowId) return;
    setIsLoading(true);
    try {
      const [wf, parts] = await Promise.all([
        apiRequest<WorkflowDetail>({
          subRoute: "workflow",
          path: `/${workflowId}`
        }),
        apiRequest<Participant[]>({
          subRoute: "workflow",
          path: `/${workflowId}/participants`
        })
      ]);
      setWorkflow(wf);
      setParticipants(parts);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSend = async () => {
    setIsSending(true);
    setActionError(null);
    try {
      await apiRequest({
        method: "POST",
        subRoute: "workflow",
        path: `/${workflowId}/send`
      });
      await load();
    } catch {
      setActionError("Failed to send workflow. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleEnd = async () => {
    setIsEnding(true);
    setActionError(null);
    try {
      await apiRequest({
        method: "POST",
        subRoute: "workflow",
        path: `/${workflowId}/end`
      });
      await load();
    } catch {
      setActionError("Failed to end workflow.");
    } finally {
      setIsEnding(false);
    }
  };

  const handleReopen = async (userId: string) => {
    setActionError(null);
    try {
      await apiRequest({
        method: "POST",
        subRoute: "workflow",
        path: `/${workflowId}/reopen/${userId}`
      });
      await load();
    } catch {
      setActionError("Failed to reopen access.");
    }
  };

  const participantColumns: ColumnsType<Participant> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: Participant) => (
        <Space>
          <Tag color={STATUS_COLORS[status] ?? "default"}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
          {record.access_override && (
            <Tooltip title="Access manually reopened">
              <Tag color="purple">Extended</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: "Sent",
      dataIndex: "sent_at",
      key: "sent_at",
      render: (d?: string) => (d ? new Date(d).toLocaleDateString() : "—")
    },
    {
      title: "Completed",
      dataIndex: "completed_at",
      key: "completed_at",
      render: (d?: string) => (d ? new Date(d).toLocaleDateString() : "—")
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, record: Participant) =>
        record.status === "overdue" && !record.access_override ? (
          <Popconfirm
            title="Reopen access for this participant?"
            onConfirm={() => handleReopen(record.user_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" icon={<UnlockOutlined />}>
              Reopen
            </Button>
          </Popconfirm>
        ) : null
    }
  ];

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Workflow not found." />
      </div>
    );
  }

  const progressPct =
    workflow.total_participants > 0
      ? Math.round(
          (workflow.completed_count / workflow.total_participants) * 100
        )
      : 0;

  return (
    <div style={{ padding: "24px 0" }}>
      <Space style={{ marginBottom: 8 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/coordinator/workflows")}
        >
          Back to Workflows
        </Button>
      </Space>

      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 24 }}
      >
        <Space align="center">
          <Title level={4} style={{ margin: 0 }}>
            {workflow.name}
          </Title>
          <Tag color={STATUS_COLORS[workflow.status]}>
            {workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)}
          </Tag>
        </Space>

        <Space>
          {workflow.status === "draft" && (
            <Popconfirm
              title="Send this workflow to all participants now?"
              onConfirm={handleSend}
              okText="Send"
              cancelText="Cancel"
            >
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={isSending}
              >
                Send
              </Button>
            </Popconfirm>
          )}
          {workflow.status === "active" && (
            <Popconfirm
              title="End this workflow? Pending participants will be marked overdue."
              onConfirm={handleEnd}
              okText="End Workflow"
              cancelText="Cancel"
            >
              <Button danger icon={<StopOutlined />} loading={isEnding}>
                End Workflow
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>

      {actionError && (
        <Alert
          type="error"
          message={actionError}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setActionError(null)}
        />
      )}

      <Descriptions bordered size="small" style={{ marginBottom: 24 }} column={2}>
        <Descriptions.Item label="Module">
          {MODULE_LABELS[workflow.module_type ?? ""] ?? workflow.module_type ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Deadline">
          {workflow.no_deadline
            ? "None"
            : workflow.deadline
              ? new Date(workflow.deadline).toLocaleDateString()
              : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Channels">
          {workflow.channels.join(", ")}
        </Descriptions.Item>
        <Descriptions.Item label="Progress">
          {workflow.completed_count} / {workflow.total_participants} completed
          {workflow.total_participants > 0 && ` (${progressPct}%)`}
        </Descriptions.Item>
        {workflow.assignment_url && (
          <Descriptions.Item label="Assignment URL" span={2}>
            <a href={workflow.assignment_url} target="_blank" rel="noreferrer">
              {workflow.assignment_url}
            </a>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Subject" span={2}>
          {workflow.subject}
        </Descriptions.Item>
        <Descriptions.Item label="Message" span={2}>
          <Text style={{ whiteSpace: "pre-wrap" }}>{workflow.body}</Text>
        </Descriptions.Item>
      </Descriptions>

      <Title level={5}>Participants ({participants.length})</Title>
      <Table
        rowKey="_id"
        columns={participantColumns}
        dataSource={participants}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No participants yet." }}
        size="small"
      />
    </div>
  );
}
